import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Save, Database, RefreshCw, Calendar, Mail, Bell, Send, Plus, X, CheckCircle, Loader2, AlertTriangle, Clock, Package, Wifi, WifiOff, Truck, Search, Trash2, FolderOpen, ToggleLeft, ToggleRight, Download, Shield, XCircle, AlertCircle, MoreHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CategoryStockSettings } from '@/components/CategoryStockSettings';
import JobMonitor from '@/components/JobMonitor';
import APInvoiceSeriesManager from '@/components/APInvoiceSeriesManager';

const Settings = () => {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [sageStatus, setSageStatus] = useState(null);
  const [sageSyncHistory, setSageSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  // Global sync state derived from lock polling - replaces local syncingIncremental/syncingFull
  const [globalSyncActive, setGlobalSyncActive] = useState(false);
  const [globalSyncOperation, setGlobalSyncOperation] = useState(null); // "incremental_sync", "full_sync", etc.
  const [syncProgress, setSyncProgress] = useState(null);
  const syncPollRef = useRef(null);
  const lockPollRef = useRef(null);  // NEW: Polling ref for global lock status
  const [savingSage, setSavingSage] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showSagePassword, setShowSagePassword] = useState(false);
  // Track if we initiated the current sync (to show result when it completes)
  const [weInitiatedSync, setWeInitiatedSync] = useState(false);
  const prevLockHeldRef = useRef(null);  // To detect lock transitions
  
  const [formData, setFormData] = useState({
    auto_order_enabled: true,
    order_schedule: 'weekly'
  });

  const [sageConfig, setSageConfig] = useState({
    sage_enabled: false,
    sage_wsdl_url: '',
    sage_service_url: '',
    sage_username: '',
    sage_password: '',
    sage_default_warehouse: '001',
  });

  const [notificationForm, setNotificationForm] = useState({
    email_notifications_enabled: false,
    notification_emails: [],
    notify_on_critical: true,
    notify_on_high_risk: true,
    notification_frequency: 'daily'
  });

  // Reconciliation state
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  const [reconciliationConfig, setReconciliationConfig] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);

  // SAGE Lock status (admin diagnostic)
  const [sageLockStatus, setSageLockStatus] = useState(null);

  // Supplier management state
  const [supplierList, setSupplierList] = useState([]);
  const [supplierSettings, setSupplierSettings] = useState({});
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierGroupFilter, setSupplierGroupFilter] = useState('all');
  const [newGroupName, setNewGroupName] = useState('');
  const [savingSuppliers, setSavingSuppliers] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchSageConfig();
    fetchNotificationSettings();
    fetchSageStatus();
    fetchSageSyncHistory();
    fetchSageLockStatus();
    fetchSupplierData();
    fetchReconciliationStatus();
    
    // Start global lock polling (every 4 seconds)
    startLockPolling();
    
    return () => {
      // Cleanup lock polling on unmount
      if (lockPollRef.current) {
        clearInterval(lockPollRef.current);
      }
    };
  }, []);
  
  // Global lock polling function - this is the SINGLE SOURCE OF TRUTH
  const startLockPolling = () => {
    // Clear any existing polling
    if (lockPollRef.current) {
      clearInterval(lockPollRef.current);
    }
    
    const pollLockStatus = async () => {
      try {
        const response = await axios.get(`${API}/sage/lock-status`);
        const lockData = response.data;
        setSageLockStatus(lockData);
        
        const wasHeld = prevLockHeldRef.current;
        const isNowHeld = lockData.held === true;
        
        // Update global sync state based on lock
        if (isNowHeld) {
          setGlobalSyncActive(true);
          setGlobalSyncOperation(lockData.lock_name);
        } else {
          setGlobalSyncActive(false);
          setGlobalSyncOperation(null);
          // Clear sync progress when lock is released (avoid showing stale progress)
          setSyncProgress(null);
        }
        
        // Detect lock release (held -> free transition)
        if (wasHeld === true && !isNowHeld) {
          // Lock was just released!
          console.log('SAGE lock released - refreshing data');
          
          // Refresh all relevant data
          await Promise.all([
            fetchSageStatus(),
            fetchSageSyncHistory(),
            fetchReconciliationStatus()
          ]);
          
          // Show result notification if we initiated this sync
          if (weInitiatedSync) {
            toast.success('Operação SAGE concluída');
            setWeInitiatedSync(false);
          }
        }
        
        prevLockHeldRef.current = isNowHeld;
      } catch (error) {
        console.error('Lock polling error:', error);
      }
    };
    
    // Initial poll immediately
    pollLockStatus();
    
    // Then poll every 4 seconds
    lockPollRef.current = setInterval(pollLockStatus, 4000);
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setFormData({
        auto_order_enabled: response.data.auto_order_enabled,
        order_schedule: response.data.order_schedule
      });
    } catch (error) {
      toast.error('Falha ao carregar definições');
    } finally {
      setLoading(false);
    }
  };

  const fetchSageConfig = async () => {
    try {
      const response = await axios.get(`${API}/sage/config`);
      setSageConfig({
        sage_enabled: response.data.sage_enabled || false,
        sage_wsdl_url: response.data.sage_wsdl_url || '',
        sage_service_url: response.data.sage_service_url || '',
        sage_username: response.data.sage_username || '',
        sage_password: '', // Never pre-fill password for security
        sage_default_warehouse: response.data.sage_default_warehouse || '001',
      });
    } catch (error) {
      console.error('Failed to load SAGE config');
    }
  };

  const fetchSageStatus = async () => {
    try {
      const response = await axios.get(`${API}/sage/status`);
      setSageStatus(response.data);
    } catch (error) {
      console.error('Failed to load SAGE status');
    }
  };

  const fetchSageSyncHistory = async () => {
    try {
      const response = await axios.get(`${API}/sage/sync-history?limit=5`);
      setSageSyncHistory(response.data.logs || []);
    } catch (error) {
      console.error('Failed to load SAGE sync history');
    }
  };

  const fetchSageLockStatus = async () => {
    try {
      const response = await axios.get(`${API}/sage/lock-status`);
      setSageLockStatus(response.data);
    } catch (error) {
      console.error('Failed to load SAGE lock status');
    }
  };

  const handleTestSageConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/sage/test-connection`);
      if (response.data.test_query) {
        toast.success(`Conexão SAGE OK! Artigo exemplo: ${response.data.sample_article?.code || 'N/A'}`);
      } else if (response.data.authenticated) {
        toast.success('Autenticação SAGE OK, mas falhou a consulta de teste');
      } else {
        toast.error(response.data.message || 'Falha na conexão');
      }
      fetchSageStatus();
    } catch (error) {
      toast.error('Erro ao testar conexão SAGE');
    } finally {
      setTestingConnection(false);
    }
  };

  // Poll for sync progress
  const pollSyncProgress = async (syncId) => {
    try {
      const url = syncId 
        ? `${API}/sage/go-live-sync-status?sync_id=${syncId}`
        : `${API}/sage/go-live-sync-status`;
      const response = await axios.get(url);
      setSyncProgress(response.data);
      
      // Stop polling if completed, error, or partial
      if (response.data.status === 'completed' || response.data.status === 'error' || response.data.status === 'partial') {
        if (syncPollRef.current) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
        }
        // Note: globalSyncActive will be cleared by lock polling when lock is released
        
        if (response.data.status === 'completed') {
          toast.success(`Sincronização concluída! ${response.data.products_synced || 0} artigos processados`);
          setPartialSync(null);
          setWeInitiatedSync(false);
          // Show SKU revalidation info if any
          if (response.data.sku_revalidation?.resolved_skus?.length > 0) {
            toast.info(`${response.data.sku_revalidation.resolved_skus.length} SKUs reclassificados automaticamente`);
          }
        } else if (response.data.status === 'partial') {
          toast.warning(`Sincronização interrompida na página ${response.data.current_page}. Use "Retomar sincronização".`);
          setPartialSync({
            sync_id: syncId,
            products_synced: response.data.products_synced,
            last_successful_page: response.data.last_successful_page,
            next_page: response.data.next_page,
            last_error: response.data.last_error,
          });
          setWeInitiatedSync(false);
        } else {
          toast.error('Erro na sincronização: ' + (response.data.errors?.join(', ') || 'Erro desconhecido'));
          setWeInitiatedSync(false);
        }
        
        // Refresh status and history
        fetchSageStatus();
        fetchSageSyncHistory();
      }
    } catch (error) {
      console.error('Error polling sync progress:', error);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }
    };
  }, []);

  // Check for existing running sync on mount
  // Partial sync state for resume
  const [partialSync, setPartialSync] = useState(null);
  
  useEffect(() => {
    const checkExistingSync = async () => {
      try {
        const response = await axios.get(`${API}/sage/go-live-sync-status`);
        
        // Check for running sync - start polling for detailed progress
        if (response.data.running) {
          setSyncProgress(response.data.running);
          // Note: globalSyncActive will be set by lock polling
          syncPollRef.current = setInterval(() => pollSyncProgress(response.data.running.sync_id), 3000);
        }
        
        // Check for partial sync (can be resumed)
        if (response.data.partial) {
          setPartialSync(response.data.partial);
        } else {
          setPartialSync(null);
        }
      } catch (error) {
        // Ignore - no existing sync
      }
    };
    checkExistingSync();
  }, []);

  const handleSageSync = async (fullSync = false) => {
    setSyncProgress(null);
    
    // Check if SAGE is already occupied (using global state)
    if (globalSyncActive) {
      const operationLabel = {
        'incremental_sync': 'Sincronização de Alterações',
        'full_sync': 'Sincronização Completa',
        'stock_reconciliation': 'Reconciliação de Stock'
      }[globalSyncOperation] || globalSyncOperation;
      toast.warning(`SAGE ocupado — ${operationLabel} em curso`);
      return;
    }
    
    try {
      if (fullSync) {
        // Full sync (Go-Live) - uses the background sync endpoint with progress tracking
        setWeInitiatedSync(true);  // Mark that we initiated this
        const response = await axios.post(`${API}/sage/sync-full-go-live`);
        
        // Check if we got a partial_exists response
        if (response.data.status === 'partial_exists') {
          toast.warning(response.data.message);
          setPartialSync({
            sync_id: response.data.partial_sync_id,
            products_synced: response.data.products_synced,
            last_successful_page: response.data.last_successful_page,
            next_page: response.data.next_page,
          });
          setWeInitiatedSync(false);
          return;
        }
        
        // Check if lock was denied
        if (response.data.status === 'lock_denied' || response.data.error?.includes('Lock SAGE ocupado')) {
          toast.warning(response.data.message || 'SAGE ocupado por outro processo');
          setWeInitiatedSync(false);
          return;
        }
        
        if (response.data.sync_id) {
          setPartialSync(null); // Clear partial since we're starting fresh
          setSyncProgress({ status: 'running', message: 'A iniciar sincronização completa...', ...response.data });
          // Start polling every 3 seconds for go-live progress
          syncPollRef.current = setInterval(() => pollSyncProgress(response.data.sync_id), 3000);
          toast.info('Sincronização completa iniciada. A monitorizar progresso...');
        } else {
          toast.success('Sincronização completa iniciada em background');
        }
      } else {
        // Incremental DBTS sync - uses the global lock polling mechanism
        setWeInitiatedSync(true);  // Mark that we initiated this
        const response = await axios.post(`${API}/sage/sync?full_sync=false`);
        
        // Check if lock was denied
        if (response.data.status === 'lock_denied' || response.data.error?.includes('Lock SAGE ocupado')) {
          toast.warning(response.data.message || 'SAGE ocupado por outro processo');
          setWeInitiatedSync(false);
          return;
        }
        
        // The global lock polling will handle tracking the sync completion
        // and refreshing data when the lock is released
        toast.info('Sincronização incremental iniciada...');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Erro ao iniciar sincronização';
      toast.error(errorMsg);
      setWeInitiatedSync(false);
    }
  };
  
  // Handle resume of a partial sync
  const handleResumeSync = async () => {
    // Check if SAGE is already occupied
    if (globalSyncActive) {
      toast.warning('SAGE ocupado — aguarde a conclusão da operação actual');
      return;
    }
    
    try {
      setWeInitiatedSync(true);
      const response = await axios.post(`${API}/sage/resume-go-live-sync`);
      
      if (response.data.sync_id) {
        setPartialSync(null); // Clear partial since we're resuming
        setSyncProgress({ 
          status: 'running', 
          message: `A retomar sincronização a partir da página ${response.data.start_page}...`,
          ...response.data 
        });
        // Start polling
        syncPollRef.current = setInterval(() => pollSyncProgress(response.data.sync_id), 3000);
        toast.info(`Sincronização retomada a partir da página ${response.data.start_page}`);
      } else {
        toast.error('Não foi possível retomar a sincronização');
        setWeInitiatedSync(false);
      }
    } catch (error) {
      toast.error('Erro ao retomar sincronização');
      setWeInitiatedSync(false);
    }
  };

  // Reconciliation functions
  const fetchReconciliationStatus = async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/reconciliation/status`),
        axios.get(`${API}/admin/reconciliation/config`)
      ]);
      setReconciliationStatus(statusRes.data);
      setReconciliationConfig(configRes.data);
    } catch (error) {
      console.error('Failed to fetch reconciliation status');
    }
  };

  const startReconciliation = async () => {
    // Check if SAGE is already occupied
    if (globalSyncActive) {
      toast.warning('SAGE ocupado — aguarde a conclusão da operação actual antes de iniciar a reconciliação');
      return;
    }
    
    setReconciliationLoading(true);
    setWeInitiatedSync(true);  // Mark that we initiated this
    try {
      const response = await axios.post(`${API}/admin/reconciliation/start`);
      
      // Check if lock was denied
      if (response.data?.success === false && response.data?.hint?.includes('lock')) {
        toast.warning(response.data.message || 'SAGE ocupado por outro processo');
        setWeInitiatedSync(false);
        setReconciliationLoading(false);
        return;
      }
      
      toast.info('Reconciliação iniciada em background. A monitorizar...');
      // Start polling for reconciliation-specific status
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/reconciliation/status`);
          setReconciliationStatus(res.data);
          if (res.data.current?.status !== 'running') {
            clearInterval(pollInterval);
            setReconciliationLoading(false);
            setWeInitiatedSync(false);
            if (res.data.current?.status === 'completed') {
              toast.success(`Reconciliação concluída: ${res.data.current?.result?.articles_updated || 0} artigos actualizados`);
            } else if (res.data.current?.status === 'cancelled') {
              toast.warning('Reconciliação cancelada');
            } else if (res.data.current?.status === 'error') {
              toast.error('Reconciliação terminou com erros');
            }
          }
        } catch (e) {
          clearInterval(pollInterval);
          setReconciliationLoading(false);
          setWeInitiatedSync(false);
        }
      }, 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao iniciar reconciliação';
      toast.error(errorMsg);
      setReconciliationLoading(false);
      setWeInitiatedSync(false);
    }
  };

  const cancelReconciliation = async () => {
    try {
      await axios.post(`${API}/admin/reconciliation/cancel`);
      toast.info('A cancelar reconciliação...');
      fetchReconciliationStatus();
    } catch (error) {
      toast.error('Erro ao cancelar reconciliação');
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setNotificationSettings(response.data);
      setNotificationForm({
        email_notifications_enabled: response.data.email_notifications_enabled,
        notification_emails: response.data.notification_emails || [],
        notify_on_critical: response.data.notify_on_critical,
        notify_on_high_risk: response.data.notify_on_high_risk,
        notification_frequency: response.data.notification_frequency
      });
    } catch (error) {
      console.error('Failed to load notification settings');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.patch(`${API}/settings`, formData);
      toast.success('Definições guardadas com sucesso');
      fetchSettings();
    } catch (error) {
      toast.error('Falha ao guardar definições');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSageConfig = async () => {
    setSavingSage(true);
    try {
      const payload = { ...sageConfig };
      // Don't send empty password (means no change)
      if (!payload.sage_password) delete payload.sage_password;
      
      await axios.put(`${API}/sage/config`, payload);
      toast.success('Configuração SAGE guardada com sucesso');
      fetchSageStatus();
      fetchSageConfig();
    } catch (error) {
      toast.error('Falha ao guardar configuração SAGE');
    } finally {
      setSavingSage(false);
    }
  };

  // ===== SUPPLIER MANAGEMENT =====
  const fetchSupplierData = async () => {
    setLoadingSuppliers(true);
    try {
      const [listRes, settingsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/suppliers/list`),
        axios.get(`${API}/supplier-settings`),
        axios.get(`${API}/supplier-groups`)
      ]);
      setSupplierList(listRes.data || []);
      // Index settings by supplier_name
      const settingsMap = {};
      (settingsRes.data || []).forEach(s => { settingsMap[s.supplier_name] = s; });
      setSupplierSettings(settingsMap);
      setSupplierGroups(groupsRes.data.groups || []);
    } catch (error) {
      console.error('Failed to load supplier data');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const toggleSupplierActive = async (supplierName) => {
    const current = supplierSettings[supplierName];
    const newActive = !(current?.active !== false); // default is true
    try {
      await axios.put(`${API}/supplier-settings/${encodeURIComponent(supplierName)}`, { active: newActive });
      setSupplierSettings(prev => ({
        ...prev,
        [supplierName]: { ...prev[supplierName], supplier_name: supplierName, active: newActive }
      }));
    } catch (error) {
      toast.error('Erro ao actualizar fornecedor');
    }
  };

  const setSupplierGroup = async (supplierName, groupName) => {
    try {
      await axios.put(`${API}/supplier-settings/${encodeURIComponent(supplierName)}`, { group: groupName || null });
      setSupplierSettings(prev => ({
        ...prev,
        [supplierName]: { ...prev[supplierName], supplier_name: supplierName, group: groupName || null }
      }));
    } catch (error) {
      toast.error('Erro ao actualizar grupo');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await axios.post(`${API}/supplier-groups`, { name: newGroupName.trim() });
      setNewGroupName('');
      const res = await axios.get(`${API}/supplier-groups`);
      setSupplierGroups(res.data.groups || []);
      toast.success('Grupo criado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar grupo');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/supplier-groups/${groupId}`);
      const res = await axios.get(`${API}/supplier-groups`);
      setSupplierGroups(res.data.groups || []);
      fetchSupplierData();
      toast.success('Grupo eliminado');
    } catch (error) {
      toast.error('Erro ao eliminar grupo');
    }
  };

  // Filtered suppliers
  const filteredSuppliers = supplierList.filter(s => {
    const matchesSearch = !supplierSearch || s.supplier.toLowerCase().includes(supplierSearch.toLowerCase());
    const settings = supplierSettings[s.supplier] || {};
    const group = settings.group || null;
    const matchesGroup = supplierGroupFilter === 'all'
      || (supplierGroupFilter === 'no-group' && !group)
      || (supplierGroupFilter === 'active' && settings.active !== false)
      || (supplierGroupFilter === 'inactive' && settings.active === false)
      || group === supplierGroupFilter;
    return matchesSearch && matchesGroup;
  });

  const activeCount = supplierList.filter(s => (supplierSettings[s.supplier]?.active) !== false).length;

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/notifications/settings`, notificationForm);
      toast.success('Definições de notificação guardadas');
      fetchNotificationSettings();
    } catch (error) {
      toast.error('Falha ao guardar definições de notificação');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Email inválido');
      return;
    }
    if (notificationForm.notification_emails.includes(newEmail)) {
      toast.error('Este email já está na lista');
      return;
    }
    setNotificationForm({
      ...notificationForm,
      notification_emails: [...notificationForm.notification_emails, newEmail]
    });
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setNotificationForm({
      ...notificationForm,
      notification_emails: notificationForm.notification_emails.filter(e => e !== email)
    });
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Introduza um email para testar');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API}/notifications/test`, { email: testEmail });
      toast.success('Email de teste enviado! Verifique a sua caixa de entrada.');
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao enviar email de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const handleTriggerNotification = async () => {
    try {
      await axios.post(`${API}/notifications/send-now`);
      toast.success('Verificação de alertas iniciada');
    } catch (error) {
      toast.error('Falha ao iniciar verificação');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="settings-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
            Definições
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Configure o sistema</p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Status Summary — inline, sóbrio */}
        <div className="flex flex-wrap items-center gap-6 text-sm border-b border-slate-200 pb-4">
          <span className={settings?.sage_connection?.status === 'connected' ? 'text-emerald-600' : 'text-red-600'}>
            <Database className="h-4 w-4 inline mr-1" />
            SAGE {settings?.sage_connection?.status === 'connected' ? 'Ligado' : 'Desligado'}
          </span>
          <span className={notificationForm.email_notifications_enabled ? 'text-emerald-600' : 'text-slate-500'}>
            <Bell className="h-4 w-4 inline mr-1" />
            Emails {notificationForm.email_notifications_enabled ? 'Activos' : 'Desligados'}
          </span>
          <span className="text-slate-600">
            <Calendar className="h-4 w-4 inline mr-1" />
            Previsão {settings?.forecast_days || 30} dias
          </span>
          <span className="text-slate-600">
            <Mail className="h-4 w-4 inline mr-1" />
            {notificationForm.notification_emails?.length || 0} destinatários
          </span>
        </div>

        {/* Email Notifications - NEW SECTION */}
        <Card className="border-2 border-red-200 rounded-sm shadow-none">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Bell className="h-5 w-5 text-red-600" />
              Notificações por Email
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-red-50/50 rounded-sm border border-red-200">
              <div>
                <p className="font-medium text-sm sm:text-base">Ativar Notificações</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Receber alertas de stock crítico
                </p>
              </div>
              <Switch
                data-testid="email-notifications-switch"
                checked={notificationForm.email_notifications_enabled}
                onCheckedChange={(checked) => setNotificationForm({ ...notificationForm, email_notifications_enabled: checked })}
              />
            </div>

            {notificationForm.email_notifications_enabled && (
              <div className="space-y-4 pt-4">
                {/* Email Recipients */}
                <div className="space-y-2">
                  <Label>Destinatários</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="exemplo@empresa.pt"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                      data-testid="add-email-input"
                    />
                    <Button type="button" variant="outline" onClick={handleAddEmail}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {notificationForm.notification_emails.map((email) => (
                      <div
                        key={email}
                        className="flex items-center gap-1 px-3 py-1 bg-slate-100 rounded-full text-sm"
                      >
                        <Mail className="h-3 w-3" />
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-1 text-slate-500 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {notificationForm.notification_emails.length === 0 && (
                    <p className="text-xs text-muted-foreground">Nenhum destinatário adicionado</p>
                  )}
                </div>

                {/* Notification Types */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-medium">Produtos Críticos</p>
                      <p className="text-xs text-muted-foreground">Stock = 0 ou rutura iminente</p>
                    </div>
                    <Switch
                      checked={notificationForm.notify_on_critical}
                      onCheckedChange={(checked) => setNotificationForm({ ...notificationForm, notify_on_critical: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div>
                      <p className="text-sm font-medium">Alto Risco</p>
                      <p className="text-xs text-muted-foreground">Abaixo do ponto de reencomenda</p>
                    </div>
                    <Switch
                      checked={notificationForm.notify_on_high_risk}
                      onCheckedChange={(checked) => setNotificationForm({ ...notificationForm, notify_on_high_risk: checked })}
                    />
                  </div>
                </div>

                {/* Frequency */}
                <div className="space-y-2">
                  <Label>Frequência das Notificações</Label>
                  <Select
                    value={notificationForm.notification_frequency}
                    onValueChange={(value) => setNotificationForm({ ...notificationForm, notification_frequency: value })}
                  >
                    <SelectTrigger data-testid="notification-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="immediate">Imediato</SelectItem>
                      <SelectItem value="daily">Diário</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* SendGrid Status */}
                <div className={`p-4 rounded-sm border ${notificationSettings?.sendgrid_configured ? 'bg-emerald-50 border-emerald-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-2">
                    {notificationSettings?.sendgrid_configured ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium text-emerald-900">SendGrid configurado</span>
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5 text-yellow-600" />
                        <span className="font-medium text-yellow-900">SendGrid não configurado</span>
                      </>
                    )}
                  </div>
                  {!notificationSettings?.sendgrid_configured && (
                    <p className="text-xs text-yellow-700 mt-2">
                      Adicione SENDGRID_API_KEY e SENDER_EMAIL ao ficheiro .env do backend para ativar o envio de emails.
                    </p>
                  )}
                </div>

                {/* Test Email */}
                <div className="space-y-2 pt-2">
                  <Label>Testar Notificação</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Email para teste..."
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="test-email-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !notificationSettings?.sendgrid_configured}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sendingTest ? 'A enviar...' : 'Enviar Teste'}
                    </Button>
                  </div>
                </div>

                {/* Last Notification */}
                {notificationSettings?.last_notification_sent && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Bell className="h-4 w-4" />
                    <span>Última notificação: {new Date(notificationSettings.last_notification_sent).toLocaleString()}</span>
                  </div>
                )}

                {/* Save Notifications Button */}
                <div className="flex justify-between pt-4 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTriggerNotification}
                    disabled={!notificationSettings?.sendgrid_configured}
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Enviar Alertas Agora
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingNotifications ? 'A guardar...' : 'Guardar Notificações'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <form onSubmit={handleSave} className="space-y-6">
          {/* SAGE Integration Config */}
          <Card className="border-2 border-blue-200 rounded-sm shadow-none">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Database className="h-5 w-5 text-blue-600" />
                Integração SAGE 100C
                {sageStatus?.configured && (
                  <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                    sageStatus.last_sync_result === 'running' 
                      ? 'bg-yellow-100 text-yellow-800'
                      : sageStatus.last_sync_result === 'success'
                      ? 'bg-green-100 text-green-800'
                      : sageStatus.last_sync_result === 'error'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {sageStatus.last_sync_result === 'running' ? 'A sincronizar...' :
                     sageStatus.last_sync_result === 'success' ? 'Sincronizado' :
                     sageStatus.last_sync_result === 'error' ? 'Erro' : 'Por sincronizar'}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              {/* Connection Status */}
              <div className={`flex items-center justify-between p-4 rounded-sm border ${
                sageStatus?.configured 
                  ? 'bg-green-50/50 border-green-200' 
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-3">
                  {sageStatus?.configured ? (
                    <Wifi className="h-5 w-5 text-green-600" />
                  ) : (
                    <WifiOff className="h-5 w-5 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium text-sm sm:text-base">
                      {sageStatus?.configured ? 'SAGE Configurado' : 'SAGE Não Configurado'}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      Armazém padrão: {sageStatus?.default_warehouse || sageConfig.sage_default_warehouse || '001'}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTestSageConnection}
                  disabled={testingConnection || !sageStatus?.configured}
                  data-testid="test-sage-connection"
                >
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Testar
                </Button>
              </div>

              {/* SAGE Config Fields */}
              <div className="space-y-4 pt-2 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 pt-2">Credenciais SOAP / WCF</p>
                
                <div className="space-y-2">
                  <Label htmlFor="sage_wsdl_url">WSDL URL</Label>
                  <Input
                    id="sage_wsdl_url"
                    placeholder="http://servidor:8083/UPWSSage.svc?wsdl"
                    value={sageConfig.sage_wsdl_url}
                    onChange={(e) => setSageConfig({ ...sageConfig, sage_wsdl_url: e.target.value })}
                    data-testid="sage-wsdl-url"
                  />
                  <p className="text-xs text-muted-foreground">Endereço completo do WSDL do serviço SAGE</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sage_service_url">Service URL</Label>
                  <Input
                    id="sage_service_url"
                    placeholder="http://servidor:8083/UPWSSage.svc"
                    value={sageConfig.sage_service_url}
                    onChange={(e) => setSageConfig({ ...sageConfig, sage_service_url: e.target.value })}
                    data-testid="sage-service-url"
                  />
                  <p className="text-xs text-muted-foreground">Endereço do serviço WCF (sem ?wsdl)</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sage_username">Utilizador</Label>
                    <Input
                      id="sage_username"
                      placeholder="Username SAGE"
                      value={sageConfig.sage_username}
                      onChange={(e) => setSageConfig({ ...sageConfig, sage_username: e.target.value })}
                      data-testid="sage-username"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sage_password">Password</Label>
                    <div className="relative">
                      <Input
                        id="sage_password"
                        type={showSagePassword ? 'text' : 'password'}
                        placeholder="Deixar vazio para não alterar"
                        value={sageConfig.sage_password}
                        onChange={(e) => setSageConfig({ ...sageConfig, sage_password: e.target.value })}
                        data-testid="sage-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSagePassword(!showSagePassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 px-2 py-1"
                      >
                        {showSagePassword ? 'Ocultar' : 'Mostrar'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sage_default_warehouse">Armazém Padrão</Label>
                  <Input
                    id="sage_default_warehouse"
                    placeholder="001"
                    value={sageConfig.sage_default_warehouse}
                    onChange={(e) => setSageConfig({ ...sageConfig, sage_default_warehouse: e.target.value })}
                    className="max-w-[200px]"
                    data-testid="sage-default-warehouse"
                  />
                </div>

                {/* Save SAGE Config */}
                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    onClick={handleSaveSageConfig}
                    disabled={savingSage}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-sage-config"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingSage ? 'A guardar...' : 'Guardar Configuração SAGE'}
                  </Button>
                </div>
              </div>

              {/* Sync Stats */}
              {sageStatus?.configured && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-200">
                  <div className="p-3 bg-blue-50/50 rounded-sm border border-blue-100 text-center">
                    <Package className="h-4 w-4 mx-auto text-blue-600 mb-1" />
                    <p className="text-lg font-bold text-blue-700">{sageStatus?.products_synced?.toLocaleString() || 0}</p>
                    <p className="text-xs text-blue-600">Sincronizados</p>
                  </div>
                  <div className="p-3 bg-green-50/50 rounded-sm border border-green-100 text-center">
                    <Plus className="h-4 w-4 mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-700">{sageStatus?.products_created?.toLocaleString() || 0}</p>
                    <p className="text-xs text-green-600">Criados</p>
                  </div>
                  <div className="p-3 bg-amber-50/50 rounded-sm border border-amber-100 text-center">
                    <RefreshCw className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                    <p className="text-lg font-bold text-amber-700">{sageStatus?.products_updated?.toLocaleString() || 0}</p>
                    <p className="text-xs text-amber-600">Atualizados</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-sm border border-slate-200 text-center">
                    <Clock className="h-4 w-4 mx-auto text-slate-500 mb-1" />
                    <p className="text-sm font-medium text-slate-700">
                      {sageStatus?.last_sync 
                        ? new Date(sageStatus.last_sync).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : 'Nunca'}
                    </p>
                    <p className="text-xs text-slate-500">Última Sync</p>
                  </div>
                </div>
              )}

              {/* Sync Actions */}
              {sageStatus?.configured && (
                <>
                {/* Explicação dos tipos de sincronização */}
                <div className="pt-4 border-t border-slate-200 mb-3">
                  <h4 className="font-medium text-slate-700 mb-2">Sincronização Manual</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    <strong>Sync Alterações:</strong> Rápido, sincroniza apenas artigos modificados (DBTS). Usar durante o dia.
                    <br />
                    <strong>Sync Completo:</strong> Pesado, percorre todos os artigos. Usar apenas em situações especiais.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSageSync(false)}
                    disabled={globalSyncActive || sageStatus?.last_sync_result === 'running'}
                    className="flex-1"
                    data-testid="sage-sync-incremental"
                    title="Sincroniza apenas artigos alterados desde o último sync (DBTS). Rápido e seguro para uso diário."
                  >
                    {globalSyncActive && globalSyncOperation === 'incremental_sync' ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {globalSyncActive && globalSyncOperation === 'incremental_sync' ? 'A sincronizar...' : 'Sync Alterações (DBTS)'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleSageSync(true)}
                    disabled={globalSyncActive || sageStatus?.last_sync_result === 'running'}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="sage-sync-full"
                    title="Sincroniza TODOS os artigos do SAGE. Processo pesado - usar apenas quando necessário."
                  >
                    {globalSyncActive && globalSyncOperation === 'full_sync' ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Database className="h-4 w-4 mr-2" />
                    )}
                    {globalSyncActive && globalSyncOperation === 'full_sync' ? 'A sincronizar...' : 'Sync Completo (Pesado)'}
                  </Button>
                </div>
                
                {/* Global SAGE Lock Status Banner */}
                {globalSyncActive && sageLockStatus && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="sage-lock-banner">
                    <div className="flex items-center gap-2 mb-2">
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                      <span className="font-semibold text-blue-800">
                        SAGE ocupado — {sageLockStatus.operation_label || sageLockStatus.lock_name || 'Operação em curso'}
                      </span>
                    </div>
                    {sageLockStatus.initiated_display && (
                      <p className="text-sm text-blue-700 mb-1">
                        Iniciado por {sageLockStatus.initiated_display}
                        {sageLockStatus.acquired_at && ` às ${new Date(sageLockStatus.acquired_at).toLocaleTimeString('pt-PT')}`}
                      </p>
                    )}
                    {sageLockStatus.duration_seconds && (
                      <p className="text-xs text-blue-600">
                        Em execução há {Math.round(sageLockStatus.duration_seconds / 60)} minutos
                      </p>
                    )}
                  </div>
                )}
                
                {/* Sync Progress Indicator (for Go-Live sync with detailed progress) */}
                {syncProgress && syncProgress.status === 'running' && globalSyncActive && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                      <span className="font-semibold text-blue-800">Sincronização em Progresso</span>
                    </div>
                    <p className="text-sm text-blue-700 mb-3">{syncProgress.message || 'A processar...'}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                      <div className="bg-white p-2 rounded border">
                        <div className="text-slate-500 text-xs">Fornecedores</div>
                        <div className="font-semibold text-lg">{syncProgress.suppliers_synced || 0}</div>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <div className="text-slate-500 text-xs">Artigos</div>
                        <div className="font-semibold text-lg">{syncProgress.products_synced || 0}</div>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <div className="text-slate-500 text-xs">Novos</div>
                        <div className="font-semibold text-lg text-green-600">{syncProgress.products_created || 0}</div>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <div className="text-slate-500 text-xs">Página</div>
                        <div className="font-semibold text-lg">{syncProgress.current_page || 0}</div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-3">
                      A actualizar automaticamente a cada 3 segundos...
                    </p>
                  </div>
                )}
                
                {/* Partial Sync Warning - Resume */}
                {partialSync && !globalSyncActive && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="partial-sync-warning">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Sincronização Incompleta</span>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                      A última sincronização foi interrompida. {partialSync.products_synced?.toLocaleString() || 0} artigos foram processados até à página {partialSync.last_successful_page || 0}.
                    </p>
                    {partialSync.last_error && (
                      <p className="text-xs text-amber-600 mb-3 font-mono bg-amber-100 p-2 rounded">
                        Erro: {partialSync.last_error}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={handleResumeSync}
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        data-testid="sage-sync-resume"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Retomar sincronização (página {partialSync.next_page})
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => {
                              if (window.confirm('Tem a certeza? Isto vai iniciar uma nova sincronização completa, ignorando o progresso anterior.')) {
                                setPartialSync(null);
                                handleSageSync(true);
                              }
                            }}
                            className="text-red-600"
                          >
                            Reiniciar sincronização completa
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
                
                {/* Link to Sync Management - Centralized Sync Control */}
                <div className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50/60" data-testid="auto-sync-controls">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Gestão de Sincronizações</span>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Configure horários, ative/desative jobs e monitorize todos os processos automáticos
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = '/sync-management'}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Gerir Sincronizações
                    </Button>
                  </div>
                </div>
                
                {/* Stock Reconciliation Section */}
                <div className="mt-4 p-4 rounded-lg border border-orange-200 bg-orange-50/60" data-testid="reconciliation-section">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-4 w-4 text-orange-600" />
                        <span className="font-medium text-slate-700">Reconciliação Completa de Stock</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        <strong className="text-orange-700">Processo pesado e demorado.</strong> Compara todos os artigos com o SAGE e corrige discrepâncias.
                        <br />
                        Usar apenas fora do expediente ou quando detectar inconsistências. Agendado automaticamente às 02:00.
                      </p>
                    </div>
                  </div>
                  
                  {/* Current Status */}
                  {reconciliationStatus?.current && (
                    <div className="mb-3 p-3 bg-white rounded border border-orange-200">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {reconciliationStatus.current.status === 'running' ? (
                            <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
                          ) : reconciliationStatus.current.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : reconciliationStatus.current.status === 'cancelled' ? (
                            <XCircle className="h-4 w-4 text-amber-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`font-medium ${
                            reconciliationStatus.current.status === 'running' ? 'text-orange-700' :
                            reconciliationStatus.current.status === 'completed' ? 'text-green-700' :
                            reconciliationStatus.current.status === 'cancelled' ? 'text-amber-700' :
                            'text-red-700'
                          }`}>
                            {reconciliationStatus.current.status === 'running' ? 'Em execução' :
                             reconciliationStatus.current.status === 'completed' ? 'Concluída' :
                             reconciliationStatus.current.status === 'cancelled' ? 'Cancelada' :
                             reconciliationStatus.current.status === 'error' ? 'Erro' : 'Inactiva'}
                          </span>
                        </div>
                        {reconciliationStatus.current.status === 'running' && (
                          <span className="text-xs text-slate-500">
                            {reconciliationStatus.current.progress || 0}% - {reconciliationStatus.current.current_message || 'A processar...'}
                          </span>
                        )}
                      </div>
                      {reconciliationStatus.current.result && (
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-slate-50 rounded">
                            <div className="font-semibold text-slate-700">{reconciliationStatus.current.result.articles_checked || 0}</div>
                            <div className="text-slate-500">Verificados</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded">
                            <div className="font-semibold text-green-700">{reconciliationStatus.current.result.articles_updated || 0}</div>
                            <div className="text-slate-500">Actualizados</div>
                          </div>
                          <div className="text-center p-2 bg-amber-50 rounded">
                            <div className="font-semibold text-amber-700">{reconciliationStatus.current.result.discrepancies?.length || 0}</div>
                            <div className="text-slate-500">Discrepâncias</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Schedule Info */}
                  {reconciliationConfig && (
                    <div className="mb-3 p-3 bg-white rounded border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-700">Agendamento Automático</span>
                        <Switch
                          checked={reconciliationConfig.enabled === true}
                          onCheckedChange={async (checked) => {
                            try {
                              const days = (reconciliationConfig.schedule_days || ['mon', 'thu']).join(',');
                              await axios.put(`${API}/admin/reconciliation/config?enabled=${checked}&schedule_hour=${reconciliationConfig.schedule_hour || 2}&schedule_minute=${reconciliationConfig.schedule_minute || 0}&schedule_days=${days}`);
                              toast.success(checked ? 'Reconciliação automática activada' : 'Reconciliação automática desactivada');
                              fetchReconciliationStatus();
                            } catch (e) {
                              toast.error('Erro ao alterar configuração');
                            }
                          }}
                          data-testid="reconciliation-enabled-toggle"
                        />
                      </div>
                      
                      {reconciliationConfig.enabled && (
                        <div className="space-y-3">
                          {/* Time Selection */}
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 w-16">Hora:</span>
                            <select
                              value={reconciliationConfig.schedule_hour || 2}
                              onChange={async (e) => {
                                try {
                                  const days = (reconciliationConfig.schedule_days || ['mon', 'thu']).join(',');
                                  await axios.put(`${API}/admin/reconciliation/config?enabled=true&schedule_hour=${e.target.value}&schedule_minute=${reconciliationConfig.schedule_minute || 0}&schedule_days=${days}`);
                                  toast.success('Hora actualizada');
                                  fetchReconciliationStatus();
                                } catch (err) {
                                  toast.error('Erro ao alterar hora');
                                }
                              }}
                              className="text-xs border rounded px-2 py-1"
                              data-testid="reconciliation-hour-select"
                            >
                              {[0,1,2,3,4,5,6,22,23].map(h => (
                                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                              ))}
                            </select>
                            <span className="text-xs text-slate-400">(fora do expediente)</span>
                          </div>
                          
                          {/* Days Selection */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 w-16">Dias:</span>
                            <div className="flex gap-1 flex-wrap">
                              {[
                                {key: 'mon', label: 'Seg'},
                                {key: 'tue', label: 'Ter'},
                                {key: 'wed', label: 'Qua'},
                                {key: 'thu', label: 'Qui'},
                                {key: 'fri', label: 'Sex'},
                                {key: 'sat', label: 'Sáb'},
                                {key: 'sun', label: 'Dom'}
                              ].map(day => {
                                const isSelected = (reconciliationConfig.schedule_days || ['mon', 'thu']).includes(day.key);
                                return (
                                  <button
                                    key={day.key}
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        const currentDays = reconciliationConfig.schedule_days || ['mon', 'thu'];
                                        let newDays;
                                        if (isSelected) {
                                          newDays = currentDays.filter(d => d !== day.key);
                                          if (newDays.length === 0) newDays = ['mon']; // Must have at least one day
                                        } else {
                                          newDays = [...currentDays, day.key];
                                        }
                                        await axios.put(`${API}/admin/reconciliation/config?enabled=true&schedule_hour=${reconciliationConfig.schedule_hour || 2}&schedule_minute=${reconciliationConfig.schedule_minute || 0}&schedule_days=${newDays.join(',')}`);
                                        toast.success('Dias actualizados');
                                        fetchReconciliationStatus();
                                      } catch (err) {
                                        toast.error('Erro ao alterar dias');
                                      }
                                    }}
                                    className={`px-2 py-1 text-xs rounded border ${
                                      isSelected 
                                        ? 'bg-orange-100 border-orange-300 text-orange-700' 
                                        : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                                    }`}
                                    data-testid={`reconciliation-day-${day.key}`}
                                  >
                                    {day.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!reconciliationConfig.enabled && (
                        <p className="text-xs text-slate-400">
                          Active o agendamento para configurar a hora e os dias da reconciliação automática.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    {reconciliationStatus?.current?.status === 'running' ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={cancelReconciliation}
                        data-testid="cancel-reconciliation-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancelar
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        disabled={reconciliationLoading || globalSyncActive}
                        onClick={startReconciliation}
                        data-testid="start-reconciliation-btn"
                        title={globalSyncActive ? 'SAGE ocupado - aguarde a conclusão da operação actual' : ''}
                      >
                        {reconciliationLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            A iniciar...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Executar Agora
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500"
                      onClick={fetchReconciliationStatus}
                      data-testid="refresh-reconciliation-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Procurement AI Automation Section */}
                <div className="mt-4">
                  <JobMonitor />
                </div>
                
                {/* AP Invoice Series Management Section */}
                <div className="mt-4">
                  <APInvoiceSeriesManager />
                </div>
                </>
              )}

              {/* SAGE Lock Status - Admin Diagnostic Panel */}
              {sageLockStatus && (
                <div className={`p-3 rounded-lg border mb-4 ${
                  sageLockStatus.status === 'free' ? 'bg-green-50 border-green-200' :
                  sageLockStatus.status === 'stale' ? 'bg-red-50 border-red-200' :
                  'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {sageLockStatus.status === 'free' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : sageLockStatus.status === 'stale' ? (
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
                    )}
                    <span className="text-sm font-medium">
                      Estado do Lock SAGE: {' '}
                      {sageLockStatus.status === 'free' ? 'Livre' :
                       sageLockStatus.status === 'stale' ? '⚠️ Expirado/Stale' :
                       'Sincronização em curso'}
                    </span>
                    <button 
                      onClick={fetchSageLockStatus}
                      className="ml-auto text-xs text-slate-500 hover:text-slate-700"
                      title="Atualizar estado"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                  </div>
                  
                  {sageLockStatus.held && (
                    <div className="text-xs space-y-1 text-slate-600">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div><span className="text-slate-500">Owner:</span> {sageLockStatus.lock_name || 'N/A'}</div>
                        <div><span className="text-slate-500">Run ID:</span> {sageLockStatus.lock_id ? sageLockStatus.lock_id.substring(0, 8) + '...' : 'N/A'}</div>
                        <div><span className="text-slate-500">Adquirido:</span> {sageLockStatus.acquired_at ? new Date(sageLockStatus.acquired_at).toLocaleString('pt-PT') : 'N/A'}</div>
                        <div><span className="text-slate-500">Expira:</span> {sageLockStatus.expires_at ? new Date(sageLockStatus.expires_at).toLocaleString('pt-PT') : 'N/A'}</div>
                        <div><span className="text-slate-500">Heartbeat:</span> {sageLockStatus.heartbeat_at ? new Date(sageLockStatus.heartbeat_at).toLocaleString('pt-PT') : 'N/A'}</div>
                        <div><span className="text-slate-500">Duração:</span> {sageLockStatus.duration_seconds ? `${Math.round(sageLockStatus.duration_seconds / 60)} min` : 'N/A'}</div>
                        {sageLockStatus.expires_in_seconds !== undefined && (
                          <div><span className="text-slate-500">TTL restante:</span> {Math.round(sageLockStatus.expires_in_seconds / 60)} min</div>
                        )}
                        <div><span className="text-slate-500">Stale:</span> {sageLockStatus.is_expired ? 'SIM' : 'NÃO'}</div>
                      </div>
                      
                      {/* Force Release Button */}
                      {user?.role === 'admin' && (
                        <div className="mt-3 pt-2 border-t border-slate-200">
                          <button
                            onClick={async () => {
                              if (!window.confirm('Tem a certeza que quer forçar a libertação do lock SAGE? Isto pode causar problemas se houver uma sincronização real em curso.')) {
                                return;
                              }
                              try {
                                await axios.post(`${API}/admin/sage-lock/force-release`);
                                toast.success('Lock SAGE libertado com sucesso');
                                fetchSageLockStatus();
                              } catch (err) {
                                toast.error('Erro ao libertar lock: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded-md font-medium"
                            data-testid="force-release-sage-lock-btn"
                          >
                            ⚠️ Forçar Libertação do Lock
                          </button>
                          <p className="text-xs text-red-600 mt-1">
                            Use apenas se o sync estiver "preso" e não estiver a progredir.
                          </p>
                        </div>
                      )}
                      {sageLockStatus.message && (
                        <div className="mt-2 pt-2 border-t border-slate-200 text-slate-700">
                          {sageLockStatus.message}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!sageLockStatus.held && sageLockStatus.last_holder && (
                    <div className="text-xs text-slate-500">
                      Último holder: {sageLockStatus.last_holder} | 
                      Libertado: {sageLockStatus.released_at ? new Date(sageLockStatus.released_at).toLocaleString('pt-PT') : 'N/A'} |
                      Razão: {sageLockStatus.release_reason || 'N/A'}
                    </div>
                  )}
                </div>
              )}

              {/* Sync History */}
              {sageSyncHistory.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium mb-2">Histórico de Sincronizações</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {sageSyncHistory.map((log) => (
                      <div key={log.id} className={`flex items-center justify-between p-2 rounded-sm text-xs ${
                        log.result === 'success' ? 'bg-green-50 border border-green-100' :
                        log.result === 'running' ? 'bg-yellow-50 border border-yellow-100' :
                        log.result === 'skipped' ? 'bg-slate-50 border border-slate-200' :
                        'bg-red-50 border border-red-100'
                      }`}>
                        <div className="flex items-center gap-2">
                          {log.result === 'success' ? (
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          ) : log.result === 'running' ? (
                            <Loader2 className="h-3 w-3 text-yellow-600 animate-spin" />
                          ) : log.result === 'skipped' ? (
                            <Clock className="h-3 w-3 text-slate-500" />
                          ) : (
                            <AlertTriangle className="h-3 w-3 text-red-600" />
                          )}
                          <span className="text-slate-700">
                            {new Date(log.timestamp).toLocaleDateString('pt-PT', { 
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                            })}
                          </span>
                          <span className="text-slate-500">por {log.started_by_user_name}</span>
                        </div>
                        <div className="text-right">
                          {log.result === 'success' && (
                            <span className="text-green-700">
                              {log.products_synced} produtos ({Math.round(log.duration_seconds || 0)}s)
                            </span>
                          )}
                          {log.result === 'running' && (
                            <span className="text-yellow-700">A processar...</span>
                          )}
                          {log.result === 'skipped' && (
                            <span className="text-slate-600" title={log.error_message}>Ignorado (lock ocupado)</span>
                          )}
                          {log.result === 'error' && (
                            <span className="text-red-700" title={log.error_message}>
                              {log.error_message ? log.error_message.substring(0, 30) + (log.error_message.length > 30 ? '...' : '') : 'Erro'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </CardContent>
          </Card>

          {/* Automated Ordering */}
          <Card className="border border-slate-200 rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Encomendas Automáticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-200">
                <div>
                  <p className="font-medium">Ativar Encomendas Automáticas</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Gerar automaticamente encomendas de compra para itens com stock baixo
                  </p>
                </div>
                <Switch
                  data-testid="auto-order-enabled-switch"
                  checked={formData.auto_order_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_order_enabled: checked })}
                />
              </div>

              {formData.auto_order_enabled && (
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="order_schedule">Agendamento de Encomendas</Label>
                    <Select
                      value={formData.order_schedule}
                      onValueChange={(value) => setFormData({ ...formData, order_schedule: value })}
                    >
                      <SelectTrigger data-testid="order-schedule-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="daily">Diário</SelectItem>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="monthly">Mensal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {settings?.last_auto_order && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Última execução automática: {new Date(settings.last_auto_order).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category stock management (drop-shipping rule) */}
          <CategoryStockSettings />

          {/* Supplier Management */}
          <Card className="border-2 border-orange-200 rounded-sm shadow-none">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Truck className="h-5 w-5 text-orange-600" />
                Gestão de Fornecedores
                <Badge variant="secondary" className="ml-auto text-xs">
                  {activeCount}/{supplierList.length} activos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
              {/* Groups Management */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Grupos / Departamentos</p>
                <div className="flex flex-wrap gap-2">
                  {supplierGroups.map(g => (
                    <div key={g.id} className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-full px-3 py-1.5">
                      <FolderOpen className="h-3 w-3 text-orange-500" />
                      <span className="text-xs font-medium text-orange-800">{g.name}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteGroup(g.id)}
                        className="ml-1 text-orange-400 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Novo grupo (ex: Fechaduras, Electrónica...)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateGroup())}
                    className="h-9 text-sm flex-1"
                    data-testid="new-group-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCreateGroup}
                    disabled={!newGroupName.trim()}
                    className="h-9"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Criar
                  </Button>
                </div>
              </div>

              {/* Search & Filter */}
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Pesquisar fornecedor..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    className="pl-8 h-9 text-sm"
                    data-testid="supplier-search"
                  />
                </div>
                <Select value={supplierGroupFilter} onValueChange={setSupplierGroupFilter}>
                  <SelectTrigger className="h-9 w-[160px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                    <SelectItem value="no-group">Sem Grupo</SelectItem>
                    {supplierGroups.map(g => (
                      <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Supplier List */}
              {loadingSuppliers ? (
                <div className="py-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto border rounded-lg divide-y divide-slate-100">
                  {filteredSuppliers.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Nenhum fornecedor encontrado</div>
                  ) : (
                    filteredSuppliers.map(s => {
                      const settings = supplierSettings[s.supplier] || {};
                      const isActive = settings.active !== false;
                      const currentGroup = settings.group || '';
                      return (
                        <div key={s.supplier} className={`flex items-center gap-3 px-3 py-2.5 ${!isActive ? 'bg-slate-50/50 opacity-60' : ''}`}
                             data-testid={`supplier-row-${s.supplier}`}>
                          {/* Toggle */}
                          <button
                            type="button"
                            onClick={() => toggleSupplierActive(s.supplier)}
                            className="flex-shrink-0"
                            data-testid={`toggle-${s.supplier}`}
                          >
                            {isActive
                              ? <ToggleRight className="h-6 w-6 text-green-500" />
                              : <ToggleLeft className="h-6 w-6 text-slate-300" />}
                          </button>
                          {/* Name + Stats */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{s.supplier}</p>
                            <p className="text-[10px] text-slate-400">
                              {s.total_products} prod. · {s.products_needing_order} enc.
                              {s.critical_items > 0 && <span className="text-red-500 ml-1">{s.critical_items} crít.</span>}
                            </p>
                          </div>
                          {/* Group Select */}
                          <Select
                            value={currentGroup || 'no-group'}
                            onValueChange={(v) => setSupplierGroup(s.supplier, v === 'no-group' ? null : v)}
                          >
                            <SelectTrigger className="h-8 w-[120px] text-xs border-dashed">
                              <SelectValue placeholder="Grupo" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="no-group">
                                <span className="text-slate-400">Sem grupo</span>
                              </SelectItem>
                              {supplierGroups.map(g => (
                                <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              <p className="text-xs text-slate-400 text-center">
                Fornecedores inativos ficam fora de toda a app: Cockpit de Compras, Sugestões AI,
                alertas, Dashboard &ldquo;Hoje&rdquo; e página &ldquo;Fornecedores&rdquo;. Reativar volta a incluí-los.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              data-testid="save-settings-button"
              disabled={saving}
              className="bg-[#C41E3A] hover:bg-[#a01830] min-w-[150px]"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'A guardar...' : 'Guardar Definições'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
